import DocumentCreationButton from 'Components/DocumentCreationButton';
import { Item } from 'Components/Grid';
import HistoryRowControls from 'Components/HistoryRowControls';
import HistoryRowListing from 'Components/HistoryRowListing';
import HistorySection from 'Components/HistorySection';
import { cerad } from 'neuro-schemas';
import { isPartialDate } from 'neuro-utils';
import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import DescriptionText from 'Routes/Cognition/utils/DescriptionText';
import { formatPartialDate } from 'Utility/date';
import { filterByDocumentType } from 'Utility/documentHandling';

const {
  utils: {
    calculateCeradTotal,
    getCeradAbnormalTasks,
    getCeradEducationYears,
    getCeradTotalCutOffScore,
    getCeradUpdatedComputedData,
  },
  config: { maxScores },
} = cerad;

const CERAD = ({ documents, startEdit, view }: IOwnProps): React.JSX.Element => {
  return (
    <HistorySection
      headerText={<FormattedMessage id="cognition.cerad.ceradLong" />}
      newButton={
        <DocumentCreationButton
          name="cerad"
          text="cognition.cerad.newCerad"
          // Implement selection of different version if/when needed
          onClick={startEdit({}, 'cerad', { version: '2023' })}
          width={15}
          height={3}
          fontSize={14}
          alternate={true}
        />
      }
      headerInfoText={<DescriptionText type={'cerad'} />}
    >
      <HistoryRowListing
        documents={filterByDocumentType(documents, 'cerad')}
        headers={
          <>
            <Item xs={2}>
              <FormattedMessage id="general.date" />
            </Item>
            <Item xs={2}>
              <FormattedMessage id="cognition.cerad.total" />
            </Item>
            <Item xs={5}>
              <FormattedMessage id="cognition.cerad.abnormalTasksCount" />
            </Item>
          </>
        }
        contentFormat={(document): React.JSX.Element => {
          const bgDocs = filterByDocumentType(documents, 'background');

          const abnormalTasks = getCeradAbnormalTasks(document, getCeradUpdatedComputedData(document, bgDocs));

          const totalScore = calculateCeradTotal(document);
          const totalCutOffScore = getCeradTotalCutOffScore(getCeradEducationYears(document, bgDocs));

          return (
            <>
              <Item xs={2}>{isPartialDate(document.date) ? formatPartialDate(document.date) : '-'}</Item>
              <Item xs={2}>
                {typeof totalScore === 'number' ? (
                  <span>
                    {totalScore}
                    <span style={{ fontWeight: 400 }}>{` / ${maxScores.total}`}</span>
                    {typeof totalCutOffScore === 'number' && (
                      <span style={{ whiteSpace: 'nowrap' }}>
                        {' ('}
                        <FormattedMessage id={`cognition.cerad.${totalScore < totalCutOffScore ? 'ab' : ''}normal`} />
                        {')'}
                      </span>
                    )}
                  </span>
                ) : (
                  '-'
                )}
              </Item>
              <Item xs={5}>{typeof totalScore === 'number' ? abnormalTasks.length : '-'}</Item>
              <Item xs={3}>
                <HistoryRowControls document={document} startEdit={startEdit} view={view} />
              </Item>
            </>
          );
        }}
      />
    </HistorySection>
  );
};

interface IOwnProps {
  documents: Array<IDocumentWithTypeV2<'cerad' | 'background'>>;
  startEdit: StartEdit;
  view?: IView;
}

export default CERAD;
